import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { playLogin } from "../lib/sounds";
import { Lock, Mail, ShieldCheck, Phone, MapPin, ArrowRight, Sparkles, Building2, Compass, BookOpen, Activity } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import { ThemeToggle } from "../components/ThemeToggle";
import LoginBackground from "../components/LoginBackground";

const QUOTES = [
  // Indian thinkers, educators & nation-builders
  { q: "Dream is not that which you see while sleeping; it is something that does not let you sleep.", a: "Dr. A.P.J. Abdul Kalam" },
  { q: "Don't take rest after your first victory because if you fail in second, more lips are waiting to say that your first victory was just luck.", a: "Dr. A.P.J. Abdul Kalam" },
  { q: "Teaching is a very noble profession that shapes the character, calibre, and future of an individual.", a: "Dr. A.P.J. Abdul Kalam" },
  { q: "The highest education is that which does not merely give us information but makes our life in harmony with all existence.", a: "Rabindranath Tagore" },
  { q: "Don't limit a child to your own learning, for he was born in another time.", a: "Rabindranath Tagore" },
  { q: "Education is the most powerful weapon which you can use to change the world.", a: "Nelson Mandela" },
  { q: "Live as if you were to die tomorrow. Learn as if you were to live forever.", a: "Mahatma Gandhi" },
  { q: "By education I mean an all-round drawing out of the best in child and man — body, mind and spirit.", a: "Mahatma Gandhi" },
  { q: "Education is the manifestation of the perfection already in man.", a: "Swami Vivekananda" },
  { q: "Take up one idea. Make that one idea your life — think of it, dream of it, live on that idea.", a: "Swami Vivekananda" },
  { q: "When educating the minds of our youth, we must not forget to educate their hearts.", a: "Dalai Lama" },
  { q: "The true teachers are those who help us think for ourselves.", a: "Dr. Sarvepalli Radhakrishnan" },
  { q: "A teacher affects eternity; he can never tell where his influence stops.", a: "Henry Adams" },
  { q: "Tell me and I forget. Teach me and I remember. Involve me and I learn.", a: "Benjamin Franklin" },
  { q: "An investment in knowledge pays the best interest.", a: "Benjamin Franklin" },
  { q: "The function of education is to teach one to think intensively and to think critically.", a: "Martin Luther King Jr." },
  { q: "The future belongs to those who learn more skills and combine them in creative ways.", a: "Robert Greene" },
  { q: "Education is not preparation for life; education is life itself.", a: "John Dewey" },
  { q: "If you want to shine like a sun, first burn like a sun.", a: "Dr. A.P.J. Abdul Kalam" },
  { q: "Arise, awake, and stop not till the goal is reached.", a: "Swami Vivekananda" },
];

const greeting = () => {
  const h = new Date().getHours();
  if (h < 5) return "Good night";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Good night";
};

const fmtNow = () =>
  new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })
  + " · " + new Date().toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showNotice, setShowNotice] = useState(false);
  const [clock, setClock] = useState(fmtNow());
  const [quoteIdx, setQuoteIdx] = useState(() => Math.floor(Math.random() * QUOTES.length));

  useEffect(() => {
    const tick = setInterval(() => setClock(fmtNow()), 30000);
    const rotate = setInterval(() => setQuoteIdx((i) => (i + 1) % QUOTES.length), 9000);
    return () => { clearInterval(tick); clearInterval(rotate); };
  }, []);

  if (user) {
    if (user.role === "ex_employee_temp") return <Navigate to="/my-offboarding" replace />;
    if (user.role === "candidate_temp") return <Navigate to="/my-onboarding" replace />;
    return <Navigate to="/" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      try { playLogin(); } catch { /* */ }
      toast.success("Welcome back");
      if (u && u.role === "ex_employee_temp") navigate("/my-offboarding");
      else if (u && u.role === "candidate_temp") navigate("/my-onboarding");
      else navigate("/");
    } catch (err) {
      setError(formatError(err.response?.data?.detail || "Login failed"));
    } finally { setLoading(false); }
  };

  const q = QUOTES[quoteIdx];

  return (
    <div className="h-screen w-screen overflow-hidden bg-[var(--pk-bg)] text-[var(--pk-text)] relative flex flex-col" data-testid="login-page">
      {/* === Floating Tech Icons + Constellation Background === */}
      <LoginBackground count={28} />

      <div className="relative z-10 flex-1 flex flex-col">
        {/* === Header === */}
        <header className="px-6 lg:px-12 pt-5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="relative">
              <BrandLogo size={48} />
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-[var(--pk-bg)]" title="System online" />
            </div>
            <div className="leading-tight">
              <div className="font-bold text-[15px] tracking-wide text-slate-900">Panchakosh Ventures</div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-indigo-700/80 font-semibold">Learning · Operations · Outcomes</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-slate-500">
              <Activity size={12} className="text-emerald-500" />
              <span className="mono">{clock}</span>
            </div>
            <button onClick={() => setShowNotice(true)} className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-slate-500 hover:text-indigo-700 transition" data-testid="login-notice-link">
              <ShieldCheck size={13} /> Authorized Use Notice
            </button>
            <ThemeToggle />
          </div>
          <div className="md:hidden">
            <ThemeToggle />
          </div>
        </header>

        {/* === Hero === */}
        <main className="flex-1 grid lg:grid-cols-[1.25fr,1fr] gap-8 px-6 lg:px-12 py-6 items-center min-h-0">
          {/* LEFT — narrative */}
          <section className="hidden lg:flex flex-col justify-center max-w-[640px]" data-testid="login-narrative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-indigo-200 bg-white/80 w-fit mb-4 text-[10.5px] uppercase tracking-[0.22em] text-indigo-800 font-semibold backdrop-blur-sm shadow-sm">
              <Compass size={12} className="text-indigo-600" /> The Ed-Tech Operations Console
            </div>

            <h1 className="text-[44px] xl:text-[54px] font-black leading-[1.02] tracking-tight text-slate-900">
              <span className="block">{greeting()}.</span>
              <span className="block">
                Let&apos;s shape{" "}
                <span className="relative inline-block">
                  <span className="text-[#002fa7] dark:text-indigo-300">tomorrow&apos;s</span>
                  <svg className="absolute -bottom-1.5 left-0 w-full" height="8" viewBox="0 0 200 8"><path d="M2 5 Q 50 1 100 5 T 198 5" stroke="#f59e0b" strokeWidth="2.5" fill="none" strokeLinecap="round" /></svg>
                </span>
                {" "}leaders,
              </span>
              <span className="block">Innovators &amp; Creators.</span>
            </h1>

            <p className="text-slate-600 text-[14px] mt-4 max-w-[540px] leading-relaxed">
              An ed-tech mission-control for the team that&apos;s helping schools and universities move forward — every dispatch, invoice, lead and outcome, in one calm place.
            </p>

            {/* Stat strip — clean white cards with colored accents */}
            <div className="grid grid-cols-3 gap-3 mt-6 max-w-[560px]" data-testid="login-stats">
              {[
                { n: "850+", l: "Institutions", accent: "border-l-rose-500", text: "text-rose-700", iconBg: "bg-rose-100" },
                { n: "5.2L+", l: "Learners Touched", accent: "border-l-teal-500", text: "text-teal-700", iconBg: "bg-teal-100" },
                { n: "4500+", l: "Indian Cities", accent: "border-l-amber-500", text: "text-amber-700", iconBg: "bg-amber-100" },
              ].map(({ n, l, accent, text, iconBg }) => (
                <div key={l} className={`relative overflow-hidden rounded-xl border border-slate-200 border-l-[4px] ${accent} bg-white p-3 transition-all hover:-translate-y-0.5 hover:shadow-lg shadow-sm`}>
                  <div className={`absolute -top-6 -right-6 w-16 h-16 rounded-full ${iconBg} blur-xl opacity-70`} />
                  <div className={`text-[24px] font-black ${text} mono leading-none`}>{n}</div>
                  <div className="text-[10.5px] uppercase tracking-wider font-bold text-slate-500 mt-1.5">{l}</div>
                </div>
              ))}
            </div>

            {/* Rotating quote — clean white card with amber accent */}
            <div className="mt-6 border-l-[3px] border-amber-500 pl-4 max-w-[560px] bg-white py-2.5 rounded-r-lg shadow-sm border-y border-r border-slate-200" data-testid="login-quote" key={quoteIdx}>
              <div className="text-[14px] italic text-slate-800 leading-snug fade-in-quote">&ldquo;{q.q}&rdquo;</div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-amber-700 font-bold mt-1.5">— {q.a}</div>
            </div>
          </section>

          {/* RIGHT — sign-in card with glassmorphism */}
          <section className="w-full max-w-[420px] mx-auto lg:mx-0 lg:justify-self-center relative" data-testid="login-card-wrap">
            {/* Soft glow halos behind the glass card */}
            <div className="absolute -inset-3 rounded-3xl bg-amber-300/35 dark:bg-amber-500/15 blur-2xl opacity-80 animate-pulse-slow" aria-hidden="true" />
            <div className="absolute -inset-3 rounded-3xl bg-indigo-300/30 dark:bg-indigo-500/15 blur-2xl opacity-60" aria-hidden="true" />

            <form
              onSubmit={submit}
              className="relative rounded-2xl p-6 overflow-hidden bg-white/70 dark:bg-white/5 backdrop-blur-2xl backdrop-saturate-150 border border-white/60 dark:border-white/10 shadow-[0_20px_60px_-15px_rgba(15,23,42,0.25)] dark:shadow-[0_20px_60px_-10px_rgba(0,0,0,0.6)]"
              data-testid="login-form"
            >
              {/* Single elegant top accent — solid IKB blue (matches favicon + brand) */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#002fa7]" aria-hidden="true" />
              {/* Subtle inner highlight (glass edge) */}
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/80 dark:via-white/20 to-transparent pointer-events-none" aria-hidden="true" />

              {/* Card top strip — 3D logo + title */}
              <div className="flex items-center justify-between mb-5 mt-1">
                <div className="flex items-center gap-3">
                  <BrandLogo size={42} />
                  <div>
                    <div className="text-[14px] font-bold leading-tight text-slate-900">Sign in</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-indigo-700 font-bold">Employee Portal</div>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.18em] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Secure
                </span>
              </div>

              {error && <div className="mb-3 px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-[12px] font-semibold" data-testid="login-error">{error}</div>}

              <div className="space-y-4">
                {/* Email field — indigo accent matching favicon brand */}
                <div>
                  <Label htmlFor="email" className="text-[10.5px] uppercase tracking-wider text-slate-700 font-bold flex items-center gap-1.5">
                    <span className="w-1 h-3 rounded-full bg-[#002fa7]" /> Work Email
                  </Label>
                  <div className="mt-2 relative group">
                    <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#002fa7] rounded-l-lg group-focus-within:bg-indigo-500 transition-colors" />
                    <Mail size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#002fa7] z-10" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="you@panchakoshventures.com"
                      className="h-12 pl-11 bg-slate-50 text-slate-900 text-[14px] border border-slate-200 hover:border-indigo-400 focus-visible:border-[#002fa7] focus-visible:ring-2 focus-visible:ring-indigo-300/50 focus-visible:bg-white placeholder:text-slate-400 caret-[#002fa7]"
                      data-testid="login-email"
                    />
                  </div>
                </div>

                {/* Password field — same indigo brand accent */}
                <div>
                  <Label htmlFor="password" className="text-[10.5px] uppercase tracking-wider text-slate-700 font-bold flex items-center gap-1.5">
                    <span className="w-1 h-3 rounded-full bg-[#002fa7]" /> Password
                  </Label>
                  <div className="mt-2 relative group">
                    <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#002fa7] rounded-l-lg group-focus-within:bg-indigo-500 transition-colors" />
                    <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#002fa7] z-10" />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="h-12 pl-11 bg-slate-50 text-slate-900 text-[14px] border border-slate-200 hover:border-indigo-400 focus-visible:border-[#002fa7] focus-visible:ring-2 focus-visible:ring-indigo-300/50 focus-visible:bg-white placeholder:text-slate-400 caret-[#002fa7]"
                      data-testid="login-password"
                    />
                  </div>
                </div>

                {/* Submit — clean solid amber CTA (no multi-color blocks per user feedback) */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold shadow-lg shadow-amber-500/40 group focus-visible:ring-2 focus-visible:ring-amber-300 border-0 transition-all hover:-translate-y-0.5 active:translate-y-0"
                  data-testid="login-submit-button"
                >
                  {loading ? "Signing in…" : (<>Enter the Console <ArrowRight size={16} className="ml-1.5 transition-transform group-hover:translate-x-1" /></>)}
                </Button>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                <span className="flex items-center gap-1.5"><Sparkles size={10} className="text-amber-500" /> Role-aware workspace</span>
                <button type="button" onClick={() => setShowNotice(true)} className="hover:text-indigo-700 transition flex items-center gap-1" data-testid="login-notice-link-mobile">
                  <ShieldCheck size={11} /> Notice
                </button>
              </div>
            </form>
          </section>
        </main>

        {/* === Bottom Ribbon — solid colors only === */}
        <footer className="relative shrink-0" data-testid="login-ribbon">
          <div className="absolute -top-3 left-6 z-10">
            <span className="inline-block bg-amber-500 text-slate-900 text-[10px] uppercase tracking-[0.22em] font-extrabold px-3 py-1 rounded-full shadow-lg shadow-amber-900/40 ring-1 ring-white/20">
              Internal Use Only
            </span>
          </div>
          <div className="bg-white border-t border-slate-200 shadow-[0_-4px_24px_-12px_rgba(0,47,167,0.15)]">
            <div className="px-6 lg:px-12 py-3 flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-5">
              <div className="text-[13px] font-bold tracking-tight">
                <span className="text-[#002fa7] dark:text-indigo-300">One portal.</span>{" "}
                <span className="text-slate-800">Every department.</span>{" "}
                <span className="text-emerald-700 dark:text-emerald-400">Zero friction.</span>
              </div>
              <div className="text-[11.5px] text-slate-600 lg:border-l lg:border-slate-200 lg:pl-5 leading-relaxed">
                HR · Sales · Accounts · Dispatch · IT · CRM — orchestrated from a single, role-aware workspace.
              </div>
              <div className="lg:ml-auto flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                <a href="tel:+917827270818" className="flex items-center gap-1.5 hover:text-indigo-700 transition font-semibold" data-testid="login-contact-phone"><Phone size={11} /> +91-78272 70818</a>
                <span className="text-slate-300">·</span>
                <a href="mailto:hello@panchakoshventures.com" className="flex items-center gap-1.5 hover:text-indigo-700 transition font-semibold" data-testid="login-contact-email"><Mail size={11} /> hello@panchakoshventures.com</a>
                <span className="text-slate-300">·</span>
                <span className="flex items-center gap-1.5"><MapPin size={11} /> Delhi, India</span>
              </div>
            </div>
            <div className="px-6 lg:px-12 py-1.5 border-t border-slate-100 flex items-center justify-end text-[10.5px] text-slate-500">
              <span>Developed by{" "}
                <a href="https://www.linkedin.com/in/jagmohanyadav/" target="_blank" rel="noreferrer" className="font-semibold text-indigo-700 hover:text-indigo-900 hover:underline" data-testid="login-developer-credit">
                  Jagmohan Yadav
                </a>
              </span>
            </div>
          </div>
        </footer>
      </div>

      {/* Confidentiality & Authorized Use Notice Dialog */}
      <Dialog open={showNotice} onOpenChange={setShowNotice}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="login-notice-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2"><ShieldCheck size={18} className="text-blue-600" /> Confidentiality &amp; Authorized Use Notice</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-[13px] leading-relaxed text-slate-700">
            <p>This portal is strictly intended for <b>authorized internal use only</b>. All information, data, systems, and resources accessible through this portal are confidential and proprietary.</p>
            <p>Any unauthorized access, copying, distribution, modification, disclosure, misuse, or attempt to compromise the security of this portal is strictly prohibited. Users must have explicit authorization to access and use the services provided herein.</p>
            <p>Any unauthorized activity, including hacking attempts, security breaches, data theft, or misuse of system resources, will be monitored, recorded, and reported to the appropriate authorities. Such actions may result in disciplinary measures, civil liability, criminal prosecution, and reporting to <b>Cyber Crime authorities</b> under applicable laws and regulations.</p>
            <p>By accessing this portal, you acknowledge and agree to comply with all security policies, confidentiality requirements, and applicable legal obligations.</p>
            <p className="font-semibold text-rose-700">Unauthorized access is prohibited and may result in legal action.</p>
            <div className="border-t pt-3 mt-3">
              <div className="text-[10.5px] uppercase tracking-wider font-bold text-blue-700 mb-2">Contact Information</div>
              <div className="space-y-1.5 text-[12.5px]">
                <div className="flex items-center gap-2"><Phone size={13} className="text-slate-400" /> +91-78272 70818</div>
                <div className="flex items-center gap-2"><Mail size={13} className="text-slate-400" /> hello@panchakoshventures.com</div>
                <div className="flex items-center gap-2"><MapPin size={13} className="text-slate-400" /> Delhi, India</div>
                <div className="flex items-center gap-2 pt-1 text-slate-500"><Building2 size={13} className="text-slate-400" /> Panchakosh Ventures — A Group of Companies</div>
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={() => setShowNotice(false)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="login-notice-close">I Understand</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .fade-in-quote { animation: fadeInUp 0.5s ease both; }
        @keyframes loginBlobDrift1 { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(40px, -20px) scale(1.05); } }
        @keyframes loginBlobDrift2 { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-30px, 25px) scale(1.08); } }
        @keyframes loginBlobDrift3 { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(20px, -30px) scale(1.06); } }
        @keyframes loginBlobDrift4 { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-25px, -15px) scale(1.1); } }
        .login-blob-1 { animation: loginBlobDrift1 18s ease-in-out infinite; }
        .login-blob-2 { animation: loginBlobDrift2 22s ease-in-out infinite; }
        .login-blob-3 { animation: loginBlobDrift3 26s ease-in-out infinite; }
        .login-blob-4 { animation: loginBlobDrift4 20s ease-in-out infinite; }
        @keyframes loginHaloPulse { 0%,100% { opacity: 0.7; } 50% { opacity: 0.95; } }
        .animate-pulse-slow { animation: loginHaloPulse 4s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
